import {
  normalizeFieldToArray,
  getClassifiedAs,
  getClassifiedAsWithClassification,
  getValueByClassification,
  getValuesByClassification,
  getAssignedBy,
  getAttributedBy,
  lookupAatFromIso,
  lookupIsoFromAat,
  getValueOrContent
} from "@thegetty/linkedart.js";

import { getIndexedId, getObjId } from "@thegetty-private/rad-app-utils";
import * as localLinkedArtHelpers from "@/helpers/LinkedArt";
import internal from "@/data/internal-ontologies";
import aat from "@/data/aat";
import { getExhibitionArray } from "./Exhibition";
import * as configHelper from "@/helpers/configHelper";
import * as routerHelpers from "@/helpers/Router";
import { removeEmpty } from "./LinkedArt";
import {
  getObjectNumber,
  findAndGetAATValue,
  convertToTitleCase,
  getName as getObjectName,
  getPlaceDepicted as getSharedPlaceDepicted,
  summarizeLicenses,
  getAccessionNumber,
  getArtistFromObject,
  getAllNames,
  getName,
  getCreationPlace,
  getCopyrightStatement,
  getClassification as getSharedClassification,
  getCreationDate,
  getDimensionStatement,
  getImageUrls,
  getInscriptions,
  getCulture,
  getMarks,
  getMaterialStatement,
  getObjectType,
  getRightsStatement as getSharedRightsStatement,
  getRightsStatementId as getSharedRightsStatementID,
  getManuscriptNumber,
  getSignatures,
  getThumbnailUrl,
  getWatermarks,
  getContentWithFormat
} from "@thegetty-private/linked-data.js";

const CARRIED_OUT_BY = "carried_out_by";
const CLASSIFIED_AS = "classified_as";
const IDENTIFIED_BY = "identified_by";

const OR = "OR";
const MEMBER_OF = "member_of";
const PART = "part";
const PART_OF = "part_of";
const PRODUCED_BY = "produced_by";
const REFERRED_TO_BY = "referred_to_by";
const SUBJECT_OF = "subject_of";
const USED_FOR = "used_for";
const CURRENT_LOCATION = "current_location";
const VISUAL_ITEM_FIELD = "shows";
const TRANSLATION = "translation";
const LANGUAGE = "language";
const DIGITALLY_CARRIED_BY = "digitally_carried_by";
const ACCESS_POINT = "access_point";
const FORMAT = "format";
const ITEMS = "items";
const DIMENSION = "dimension";
export const PART_HEADING_LOCALE_KEY = "part_heading";
export const GROUP_HEADING_LOCALE_KEY = "group_heading";
export const WHOLE_HEADING_LOCALE_KEY = "whole_heading";

// Defines ComplexObject types
const MANUSCRIPT = "manuscript";
const SET = "set";
const FRAGMENT = "fragment";

/**
 * Gets the AAT Actors for an object
 *
 * @param {object} object  - the human made object
 * @param {array} aatQualifiers - a list of aat qualifiers
 * @param {string} lang  - the language
 *
 * @returns {array}
 */
export function getActors(object, aatQualifiers, lang) {
  return getArtistFromObject(object, aatQualifiers, lang);
}

/**
 * Gets the Assigned Producers (Generally previously assigned) from a HumanMadeObject (JSON-LD)
 *
 * @param {object} humanMadeObject - the JSON-LD HumanMadeObject
 *
 * @returns {array} the list of producers from the JSON-LD
 */
export function getAssignedProducers(humanMadeObject) {
  let previous_producers_attributed = getAssignedBy(
    humanMadeObject.produced_by,
    PART_OF
  );
  return previous_producers_attributed;
}

/**
 * Gets a list of the "previously" attributed artists
 *
 * @param {object} humanMadeObject - the human made object
 *
 * @returns {array} of LOD objects representing Actors (People/Groups)
 */
export function getPreviouslyAttributedProducers(humanMadeObject) {
  if (humanMadeObject == undefined) {
    return [];
  }

  let previous_producers_attributed = getAssignedProducers(humanMadeObject);
  let previous_producers = [];

  previous_producers_attributed.forEach((attributed) => {
    normalizeFieldToArray(attributed, CARRIED_OUT_BY).forEach((producer) => {
      previous_producers.push(producer);
    });
  });
  return previous_producers;
}
/**
 * gets the alternate titles
 * @param {object} object  - the JSON-LD Object
 * @param {function} translate - the translate function from Vue18n
 *
 * @returns {array}
 */
export function getAlternateTitles(object, translate) {
  let toReturn = [];
  let seen = new Set();
  // initialize the alternate titles with the current title
  seen.add(cleanupTitle(getObjectName(object)));

  // all of the titles to grab, and display in the appropriate order
  const titleConfig = [
    {
      aat: internal.TRANSLATED_TITLE,
      localeKey: "object_metadata.translated_title"
    },
    { aat: internal.GROUP_TITLE, localeKey: "object_metadata.group_title" },
    {
      aat: internal.PUBLISHED_TITLE,
      localeKey: "object_metadata.published_title"
    },
    { aat: aat.DISPLAY_TITLE, localeKey: "object_metadata.display_title" },
    { aat: aat.ALTERNATE_TITLE, localeKey: "object_metadata.alt_title" }
  ];

  const omitClassifications = [aat.EXHIBITION_TITLE];

  for (const pair of titleConfig) {
    addTitlesByType(
      object,
      pair.aat,
      seen,
      pair.localeKey,
      translate,
      toReturn,
      omitClassifications
    );
  }
  return toReturn;
}

/**
 * Cleans up the title by replacing fancy quotes or italics with normal quotes and markdown
 *
 * @param {string} str - title
 *
 * @returns {str}
 */
function cleanupTitle(str) {
  if (str == undefined) {
    return undefined;
  }
  str = str.replace(/<\/?i>/gi, "_");
  return replaceFancyQuotes(str);
}

/**
 * Replaces curly / fancy quotes with their non-curly versions
 * @param {string} str - the string
 * @returns
 */
function replaceFancyQuotes(str) {
  if (str == undefined) {
    return str;
  }
  return str.replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"');
}

/**
 *  Helper function that returns the object's title(s) with a qualifier
 *
 * @param {object} object - the object to inspect
 * @param {String} qualifier - the URI based qualifier
 *
 * @returns {array} the alternate title(s)
 */
export function getTitlesWithQualifier(object, qualifier) {
  const identified_by = normalizeFieldToArray(object, IDENTIFIED_BY);
  if (identified_by.length == 0) {
    return undefined;
  }
  const names = identified_by.filter(
    (elem) => elem.type === "Name" && elem.content != undefined
  );

  return getClassifiedAs(names, qualifier);
}

/**
 * Handles the deduping and management of duplicate titles in the titles list.
 *
 * @private
 * @param {object} object - the JSON-LD object
 * @param {string} classification - the AAT / internal classification
 * @param {set} seen - the list of seen titles (to reduce dups)
 * @param {string} localeKey  - the locale key for the title
 * @param {function} translate - the translation function
 * @param {array} accumulator - the array to add titles to
 * @param {array} omitClassifications - aat classifications to omit
 */
function addTitlesByType(
  object,
  classification,
  seen,
  localeKey,
  translate,
  accumulator,
  omitClassifications = []
) {
  let titles = getTitlesWithQualifier(object, classification) || [];

  titles = titles.concat(
    getClassifiedAs(getAttributedBy(object, IDENTIFIED_BY), classification)
  );

  // filter out exhibition titles
  titles.forEach((alt) => {
    if (
      getClassifiedAs(alt, omitClassifications, { operator: OR }).length > 0
    ) {
      return;
    }
    let title = cleanupTitle(getValueOrContent(alt));
    if (seen.has(title)) {
      return;
    }
    accumulator.push(translate(localeKey, { title: title }));
    seen.add(title);
  });
}

/**
 * Takes artists array returned from peopleDetailsByManMadeObject getter, returns artist + detail strings
 *
 * @param {array} artists - Array of artists returned from peopleDetailsByManMadeObject getter
 * @return {array} Array of strings ["Artist (Nationality birth - death)"]
 */
export function getArtistWithDetailsString(artists) {
  // helper to check if human made object field value is valid

  let artistCaption;
  let artistCaptions = [];
  if (!artists || !artists.length) {
    return;
  }
  artists.forEach((artist) => {
    artistCaption = "";
    if (!isValid(artist) || !isValid(artist.name)) {
      return;
    }

    artistCaption = artistCaption + artist.name;
    if (isValid(artist.afterLink)) {
      artistCaption += " " + artist.afterLink;
    }
    artistCaptions.push(artistCaption);
  });

  return artistCaptions.join(", ");
}

/**
 * Takes an array of bibliographic references and adds a link if found in linguisticObjects store
 *
 * @param {array} references - The array of bibliographic references
 * @param {array} linguisticObjects - The array of linguistic objects from the store/state
 * @param {object} humanMadeObject - the HumanMadeObject JSON-LD
 * @return {array} Updated array with links if available
 *
 */
export function getBibliographicLinks(
  references,
  linguisticObjects = [],
  humanMadeObject = { referred_to_by: [] }
) {
  if (!references) {
    return undefined;
  }

  // filter out undefined objects
  linguisticObjects = linguisticObjects.filter((o) => o != undefined);

  if (
    humanMadeObject == undefined ||
    humanMadeObject[REFERRED_TO_BY] == undefined
  ) {
    return undefined;
  }
  return references.map((reference) => {
    const object = linguisticObjects.find(
      (lobject) => getIndexedId(lobject.id) == reference.linguisticObjectId
    );

    if (object && object.subject_of) {
      const subjectOf = object.subject_of;
      const linkObject = getClassifiedAs(subjectOf, aat.WEB_PAGES);

      reference.url = linkObject[0].id;
    }

    // get ref from humanMadeObject
    // if it has a subject_to, use that as it's a object specific URL
    let ref = humanMadeObject[REFERRED_TO_BY].map((ref) => {
      if (
        ref &&
        ref.about &&
        ref.about.length > 0 &&
        ref.subject_of &&
        ref.subject_of.length > 0
      ) {
        if (
          getIndexedId(ref.about[0].id) ==
          getIndexedId(reference.linguisticObjectId)
        ) {
          return ref.subject_of[0].id;
        }
      }
    }).filter((x) => x != undefined);

    // override the URL if needed
    if (ref[0] != undefined) {
      reference.url = ref[0];
    }

    return reference;
  });
}

/**
 * Takes a HumanMadeObject and returns an array of the bibliographic references
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @return {array} Array of bibliographic references
 *
 */
export function getBibliographicReferences(object) {
  const referredToBy = normalizeFieldToArray(object, REFERRED_TO_BY);
  if (!referredToBy.length) {
    return undefined;
  }

  return getClassifiedAs(referredToBy, aat.BIBLIOGRAPHIC_REFERENCES);
}

/**
 * Takes a HumanMadeObject and returns an array of bibliographic data objects
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @return {array} Array of bibliographic tab data
 *
 */
export function getBibliographicTabData(object) {
  const bibliographicRefs = getBibliographicReferences(object);
  if (!bibliographicRefs) {
    return undefined;
  }

  return bibliographicRefs
    .map((ref) => {
      return {
        content: ref.content,
        order: getOrder(ref),
        linguisticObjectId: getLinguisticObjectId(ref)
      };
    })
    .sort((a, b) => a.order - b.order);
}

/**
 * Takes a HumanMadeObject and its id and returns the caption details
 *
 * @param {string} id - The HumanMadeObject ID
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @return {object} The caption details
 *
 */
export function getCaptionDetails(id, object) {
  return {
    title: getObjectName(object),
    date: getDateCreated(object),
    id: id,
    medium: getMaterialStatement(object),
    dimensions: getDimensionStatement(object),
    culture: getCultures(object),
    object_number: getAccessionNumber(object),
    credit_line: getCreditLine(object, true),
    dor_id: localLinkedArtHelpers.getDORID(object)
  };
}

/**
 * Takes a HumanMadeObject and get the classification.
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @return string - the aat value / classification
 *
 */
export function getClassification(object, aatQualifiers) {
  const classificationAAT = getSharedClassification(object, aatQualifiers);

  if (!classificationAAT) {
    return undefined;
  }

  return convertToTitleCase(classificationAAT);
}

/**
 * gets the object's credit line
 * @param {object} object  - the JSON-LD Object
 * @param {boolean} includeSourceCreditLine - whether to include the source credit line or not
 *
 * @returns {string}
 */
export function getCreditLine(object, includeSourceCreditLine = false) {
  const creditLines = getClassifiedAs(
    normalizeFieldToArray(object, REFERRED_TO_BY),
    aat.CREDIT_LINE
  );

  let standardVal = getValueByClassification(
    normalizeFieldToArray(object, REFERRED_TO_BY),
    internal.STANDARD_CREDIT_LINE
  );

  // filter the credit lines, Prefer the "Standard value" (the one without the 'Source') over the source value
  let sourceVal = undefined;
  creditLines.forEach((credit) => {
    let val = credit.content;
    let isSource = false;
    let skipCreditLink = false;
    credit[CLASSIFIED_AS].forEach((cls) => {
      if (cls.id == aat.SOURCES) {
        isSource = true;
      }
    });

    credit[CLASSIFIED_AS].forEach((cls) => {
      if (cls.id == aat.REPRODUCTIONS) {
        skipCreditLink = true;
      }
    });
    if (skipCreditLink) {
      return;
    }
    // prefer 1st value (so we consistently choose)
    if (isSource && sourceVal == undefined) {
      sourceVal = val;
    } else if (standardVal == undefined) {
      standardVal = val;
    }
  });

  if (sourceVal != undefined && includeSourceCreditLine == true) {
    return sourceVal;
  }
  return standardVal;
}

/**
 * gets the culture(s)
 * @param {object} - the JSON-LD object
 *
 * @returns {string} cultures string or undefined
 */
export function getCultures(object) {
  let cultures = getCulture(object);
  if (cultures.length > 0) {
    return cultures.join(", ");
  }
  return undefined;
}

/**
 * gets the date created (or dates)
 * @param {object} object  - the JSON-LD Object
 * @param {array} aatQualifiers - the list of AAT values used to construct the dates
 * @param {string} lang - the language to assume we're using
 *
 * @returns {array}
 */
export function getDateCreated(object, aatQualifiers, lang) {
  const dates = getCreationDate(object, aatQualifiers, lang);
  if (Array.isArray(dates)) {
    return dates[0];
  }
  return dates;
}

/**
 * Takes a HumanMadeObject and returns an array of the education resources references
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @return {array} Array of education references
 *
 */
export function getEducationReferences(object) {
  const referredToBy = normalizeFieldToArray(object, REFERRED_TO_BY);
  if (!referredToBy.length) {
    return undefined;
  }

  return getClassifiedAs(referredToBy, aat.INSTRUCTIONAL_MATERIALS);
}

/**
 * Takes an array of education resources and adds additional details from linguisticObjects store
 *
 * @param {array} references - The array of education resources
 * @param {array} linguisticObjects - The array of linguistic objects from the store/state
 * @return {array} Updated array with additional details from linguisticObjects store
 *
 */
export function getEducationResourcesDetails(references, linguisticObjects) {
  if (!references || !references.length || !linguisticObjects) {
    return undefined;
  }

  // for each reference object, find the matching linguisticObject with additional data
  const pairs = references.map((object) => {
    const lObject = linguisticObjects.find(
      (lObject) =>
        getIndexedId(lObject.id) == getIndexedId(object.linguisticObjectId)
    );
    return { object: object, lObject: lObject };
  });

  /* for each pair produced from the above mapping, check the linguisticObject's referred_to_by
  object to see if it has additional details (url, description, format, grades & subjects)
  if any of those fields are found, add it to the original reference object */
  const enhancedObjectDetails = pairs.map((pair) => {
    // first, check for a url and add it to the object details
    const subjectOf = normalizeFieldToArray(pair.lObject, SUBJECT_OF);

    if (subjectOf.length) {
      const object = pair.object;
      const linkObject = getClassifiedAs(subjectOf, aat.WEB_PAGES);

      object.url = linkObject[0].id;
    }

    // next, check for the additional fields to add to the object
    const referredToBy = normalizeFieldToArray(pair.lObject, REFERRED_TO_BY);

    const fields = [
      { label: "description", aat: aat.DESCRIPTIVE_NOTE },
      { label: "format", aat: aat.FORMAT },
      { label: "grades", aat: aat.AGE_GRADES },
      { label: "subjects", aat: aat.SUBJECTS }
    ];

    return fields.reduce((acc, field) => {
      let fieldDetails = getClassifiedAs(referredToBy, field.aat);

      if (fieldDetails.length) {
        acc[field.label] = fieldDetails[0].content;
      }

      return acc;
    }, pair.object);
  });

  return enhancedObjectDetails;
}

/**
 * Takes a HumanMadeObject and returns an array of education resources data objects
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @return {array} Array of education resources tab data
 *
 */
export function getEducationResourcesTabData(object) {
  const educationRefs = getEducationReferences(object);
  if (!educationRefs) {
    return undefined;
  }

  return educationRefs
    .map((ref) => {
      return {
        title: ref.content,
        order: getOrder(ref),
        linguisticObjectId: getLinguisticObjectId(ref)
      };
    })
    .sort((a, b) => a.order - b.order);
}

/**
 * Takes a HumanMadeObject and get an object containing the enhanced data needed for the HumanMadeObject home page tabs.
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @param {array} linguisticObjects - the array of linguistic objects
 * @param {array} exhibitionData - an array with [acitivity objects, propositional objects, place objects]
 * @return {object} The enhanced tabs data
 *
 */
export function getEnhancedTabsData(
  object,
  linguisticObjects,
  exhibitionData,
  aat,
  logger
) {
  const tabsData = getTabsData(object);
  let exhibitionArray;
  if (exhibitionData && exhibitionData.exhibitions) {
    exhibitionArray = getExhibitionArray(exhibitionData, logger);
  }
  return {
    exhibitions: exhibitionArray,
    bibliography: getBibliographicLinks(
      tabsData.bibliography,
      linguisticObjects
    ),
    education_resources: getEducationResourcesDetails(
      tabsData.education_resources,
      linguisticObjects
    )
  };
}

/**
 * Takes a HumanMadeObject and returns an array of essay references
 * @deprecated
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @return {array} Array of essay ids/urls
 *
 */
export function getEssayIDs(object) {
  const referredToBy = normalizeFieldToArray(object, REFERRED_TO_BY);
  if (!referredToBy.length) {
    return undefined;
  }

  const essayObjects = getClassifiedAs(referredToBy, aat.ESSAYS);

  return essayObjects.map((object) => object.id);
}

/**
 * Takes a HumanMadeObject and returns an array of essay references
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @return {array} Array of essay links
 *
 */
export function getEssayLinks(object) {
  const referredToBy = normalizeFieldToArray(object, REFERRED_TO_BY);
  const essayObjects = getClassifiedAs(referredToBy, aat.ESSAYS);

  return essayObjects.map((e) => {
    const identifiedBy = normalizeFieldToArray(e, IDENTIFIED_BY);
    const linkText = getValueByClassification(
      identifiedBy,
      aat.PREFERRED_TERMS
    );
    return { iconName: "outbound", url: e.id, text: linkText };
  });
}

/**
 * Takes a HumanMadeObject and returns an array of the associated exhibition objects' IDs.
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @return {array} Array of exhibition object IDs
 *
 */
export function getExhibitionIDs(object) {
  let returnArray = [];
  let memberOf = normalizeFieldToArray(object, MEMBER_OF);
  for (let set of memberOf) {
    let usedFor = normalizeFieldToArray(set, USED_FOR);
    returnArray = returnArray.concat(usedFor);
  }
  return returnArray.map((x) => x.id);
}

/**
 * Takes a HumanMadeObject and returns an array of the associated exhibition objects'
 * activity objects' indexedIDs to be used for indexing member_of_exhibition
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @return {array} Array of exhibition object's activity objects' indexedIDs
 * @example [
 *  "activity/d0128885-d65e-4d92-8ae7-082e237f7445",
 *  "activity/54fed9c6-4be6-4f6c-9db3-3781ad92aafe",
 * ])
 *
 */
export function getExhibitionIndexedIDs(object) {
  // result is an array of fullIds
  const exhibitionIds = getExhibitionIDs(object);

  // map them to be indexed/object IDs
  return exhibitionIds.map((exhibitionId) => getIndexedId(exhibitionId));
}

/**
 * Gets the group ID for a HumanMadeObject
 * The group ID is the indexed ID of the group's parent object
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @return {string} The group ID (example: "object/f112e473-f35b-441f-9adb-7cbdc94992c8")
 *
 */
export function getGroupID(object) {
  const relatedWorks = getRelatedWorks(object);
  const relatedWorksCount = relatedWorks.length;

  // if there's only 1 related work, the object is part of a group
  if (relatedWorksCount == 1) {
    return relatedWorks[0];
  }
  // if there are >1 related works, the object is the parent of a group
  if (relatedWorksCount > 1) {
    return getObjId(object);
  }
  // if there are no related works, the object is not part of a group
  return undefined;
}

// This could change in the future. Will we have full VisualItems to fetch? Will we need to?
// Will IIIF make this totally unnecessary?

/**
 * Takes a HumanMadeObject and checks for images. Returns true or false.
 * @param {object} object - The JSON-LD for HumanMadeObject
 */
export function getImageBoolean(object) {
  const images = getImageUrls(object);
  if (images == undefined) {
    return false;
  }
  return true;
}

/**
 * gets the linguistic object id of the object
 * @param {object} object  - the JSON-LD Object
 *
 * @returns {string} linguistic object id if available
 */
function getLinguisticObjectId(object) {
  // if the object has an about field or an identified by, return the id; if not, return undefined
  return object.about ? getIndexedId(object.about[0].id) : undefined;
}

/**
 * Takes a HumanMadeObject and gets the metadata fields
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @param {object} classification - the classification
 * @param {object} department - the department
 * @param {array} producers - the artists
 * @param {array} previous_producers - the previous artists
 * @param {function} translate - the translate function
 *
 * @return {object} The HMO metadata fields
 *
 */
export function getMetadataFields(
  object,
  classification,
  department,
  producers,
  previous_producers,
  fragmentParts,
  translate
) {
  let places = [
    ...linkifyPlace(
      getCreationPlace(object),
      translate("object_metadata.place_created_qual")
    ),
    ...linkifyPlace(
      getPlaceFound(object),
      translate("object_metadata.place_found_qual")
    ),
    ...linkifyPlace(
      getPlaceDepicted(object),
      translate("object_metadata.place_depicted_qual")
    )
  ];

  let obj = {
    title: getObjectName(object),
    artist: producers,
    date: getDateCreated(object),
    medium: linkify(getMaterialStatement(object), "materials"),
    dimensions: getDimensionStatement(object),
    place: places,
    culture: linkify(getCulture(object), "culture"),
    subject_tags: getSubjectTags(object),
    signatures: getSignatures(object),
    object_number: getObjectNumber(object),
    parts_of_object: fragmentParts,
    parts_of_group: fragmentParts,
    copyright: getCopyrightStatement(object, aat.COPYRIGHT),
    credit_line: getCreditLine(object),
    inscriptions: getInscriptions(object),
    marks: getMarks(object),
    watermarks: getWatermarks(object),
    alternate_titles: getAlternateTitles(object, translate),
    previous_attribution: previous_producers,
    department: linkify(department, "department"),
    classification: linkify(classification, "classification"),
    object_type: linkify(getObjectType(object), "object_type"),
    alternate_numbers: getAlternateNumbers(object, translate)
  };

  if (getRecordStructureLocaleKey(object) != GROUP_HEADING_LOCALE_KEY) {
    delete obj.parts_of_group;
  } else {
    delete obj.parts_of_object;
  }

  Object.keys(obj).forEach((key) => {
    if (
      obj[key] == undefined ||
      (Array.isArray(obj[key]) && obj[key].length == 0)
    ) {
      delete obj[key];
    }
  });
  return obj;
}

/**
 * Given a lists of artists, constructs a list of previously attributed artist names (with links)
 *
 * @param {object} humanMadeObject - the human made object
 * @param {array} previous_producers - (a list of LOD Actors)
 *
 * @returns {array} - returns a list of context objects
 */
export function getPreviousProducerAttributions(
  humanMadeObject,
  previous_producers
) {
  let producers = [];
  if (
    humanMadeObject == undefined ||
    (previous_producers.length > 0 &&
      localLinkedArtHelpers.filterUndefined(previous_producers).length == 0)
  ) {
    return producers;
  }

  let previous_producers_attributed = getAssignedProducers(humanMadeObject);
  previous_producers_attributed.forEach((producer) => {
    if (producer == undefined) {
      return;
    }
    let context = constructProducerContextObject(producer, previous_producers);
    producers.push(context);
  });
  return producers;
}

/**
 * Builds the actor route from an Actor object
 *
 * @param {object} artist
 * @param {string} slug
 *
 * @returns {object} - the VueRouter link object
 */
export function buildActorRoute(artist, slug) {
  // add router link "to" property
  const type = artist.type;
  const routeName = type == "Group" ? "group" : "person";
  const indexedId = getObjId(artist);

  return {
    name: routeName,
    state: { indexedId: indexedId, slug: slug },
    params: { slug: slug }
  };
}

/**
 * Takes a HumanMadeObject and returns the producers to display artist/maker in metadata section
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @return {array} Array of strings and/or artist/producer objects. Example:
 *  [
 *    "Unknown Artist",
 *    {
 *      name: "string",
 *      description: "String".
 *    }
 *  ]
 */
export function getMetadataProducers(object) {
  let artistLinks = [];
  const producers = getProducers(object);
  const dedupe = configHelper.hideArtistDupes();

  // create link object for each producer (to be displayed in metadata)
  producers.forEach((producer) => {
    const carriedOutBy = normalizeFieldToArray(producer, CARRIED_OUT_BY);
    const referredToBy = normalizeFieldToArray(producer, REFERRED_TO_BY);

    // if maker is unknown, push description (name with prefix/suffix) to return array
    if (isMakerKnown(carriedOutBy) === false) {
      artistLinks.push(
        removeEmpty({
          name: getValueByClassification(referredToBy, internal.PRODUCER_NAME),
          beforeLink: getValueByClassification(
            referredToBy,
            internal.PRODUCER_NAME_PREFIX
          ),
          afterLink: getValueByClassification(
            referredToBy,
            internal.PRODUCER_NAME_SUFFIX
          ),
          role: getProducerRole(producer)
        })
      );

      return;
    }

    let producerInfo = constructProducerContextObject(producer);
    if (producerInfo == undefined) {
      return undefined;
    }

    artistLinks.push(producerInfo);
    if (dedupe && artistLinks.length > 1) {
      artistLinks = dedupeArtists(artistLinks, "id");
    }
  });

  return artistLinks;
}

/**
 * Converts a producer context object into a link object that we can reuse
 *
 * @param {object} producerInfo - The context object generated from constructProducerContextObject
 * @param {string} slug - the slug to associate with the link
 *
 * @returns {object|string} a VueRouter and GuiLink compatible link object or display string
 */
export function convertProducerContextToLink(producerInfo, slug) {
  // if name is undefined, we're looking at something that's not a link
  if (producerInfo == undefined || producerInfo.name == undefined) {
    return producerInfo;
  }

  // if producer has properties, construct link object for delimitedFieldValues
  let afterLink;
  if (
    producerInfo.suffix != undefined &&
    producerInfo.suffix.trim().length > 0
  ) {
    afterLink = producerInfo.suffix.trim();
  }
  if (producerInfo.nationality_and_dates != undefined) {
    afterLink = afterLink ? `${afterLink} ` : "";
    afterLink += `(${producerInfo.nationality_and_dates.trim()})`;
  }

  let linkObj = {
    id: producerInfo.id,
    name: producerInfo.name,
    beforeLink: producerInfo.prefix,
    afterLink: afterLink
  };
  linkObj = localLinkedArtHelpers.stripEmptyObjectParts(linkObj);

  if (producerInfo.id != undefined && slug != undefined) {
    linkObj.to = buildActorRoute(producerInfo, slug);
  }

  return linkObj;
}

/**
 * Given an array of object or an object it'll convert them into a VueRouter compatible link that searches the site
 *
 * @param {string|array} data - a list of objects or an object
 * @param {string} term - the query field
 * @param {boolean} lowercase - whether to lowercase the search (default false)
 *
 * @returns {array} - an array of links
 */
function linkify(data, term, lowercase = false) {
  let toReturn = [];
  let objects = data;
  if (Array.isArray(data) == false) {
    objects = [data];
  }
  for (let item of objects) {
    if (item == undefined || item.trim() == "") {
      continue;
    }
    toReturn.push({
      name: item,
      to: {
        name: "search",
        query: {
          [term]: lowercase ? item.toLowerCase() : item
        }
      }
    });
  }
  return toReturn;
}

/**
 * Given an array of fragment part objects, it will convert each one into VueRouter compatible link object
 * that will route to that fragment object's page
 *
 * @param {object} humanMadeObject - the human made object
 * @param {array} fragmentParts - a list of fragment part search result objects
 * @param {string} parentId - the parentId (indexed) of the fragments
 * @param {function} translatePart - the translatePart localization mixin
 * @param {function} localizeNumber - the localizeNumber localization mixin
 *
 * @returns {array|undefined} - an array of link objects
 */
export function linkifyFragments(
  object,
  fragmentParts,
  parentId,
  translatePart,
  localizeNumber
) {
  if (!fragmentParts) {
    return undefined;
  }

  // FIXME: comps show 25, using a smaller amount for now
  const MAX_FRAGMENTS_TO_DISPLAY = configHelper.getMaxFragmentsToDisplay();
  const truncateFragmentList =
    fragmentParts.length > MAX_FRAGMENTS_TO_DISPLAY ? true : false;

  let fragmentPartsToLink = truncateFragmentList
    ? fragmentParts.slice(0, MAX_FRAGMENTS_TO_DISPLAY)
    : fragmentParts;

  let fragmentLinks = fragmentPartsToLink.map((part) => {
    return {
      name: translatePart("object_metadata.fragment_links.part_link", {
        name: part.primary_name,
        object_number: part.object_number
      }),
      to: {
        name: "object",
        state: { indexedId: part.id, slug: part.id_manager_slug },
        params: { slug: part.id_manager_slug }
      }
    };
  });

  if (truncateFragmentList) {
    const to = {
      name: "search",
      query: {
        parent_id: parentId,
        include_deaccessioned: true
      }
    };

    fragmentLinks.push({
      name: translatePart("object_metadata.fragment_links.view_all", {
        number: localizeNumber(fragmentParts.length)
      }),
      to: to
    });
  }

  return fragmentLinks;
}

/**
 * Given an array of places, it will convert them into VueRouter compatible link objects
 * that will perform a phrase search
 *
 * @param {array} places - a list of places
 * @param {string} suffix - the suffix for the place name for display
 *
 * @returns {array} - an array of link objects
 */
function linkifyPlace(places, suffix) {
  if (!places) {
    return [];
  }

  let placeLinks = [];

  for (let place of places) {
    if (place == undefined || place.trim() == "") {
      continue;
    }

    placeLinks.push({
      afterLink: suffix,
      name: place,
      to: {
        name: "search",
        query: { q: `"${place}"` }
      }
    });
  }

  return placeLinks;
}

/**
 * Takes a Producer entry for a HumanMadeObject and converts it to an object that can be shared between the frontend and the indexer
 *
 * @param {object} object - The Producer part of a HumanMadeObject
 * @param {array} people - An array of Person JSON-LD related to the HumanMadeObject
 * @param {array} aatQualifiers - an array of AAT JSON-LD Documents to reference
 * @param {boolean} localize - whether to add string formatting logic
 *
 * @return {object} Array of producer objects [ { id: "string", name: "string", prefix: "string", suffix: "string" } ]
 */

export function constructProducerContextObject(
  producer,
  people,
  aatQualifiers,
  localize = true
) {
  let context = {};
  const carriedOutBy = normalizeFieldToArray(producer, CARRIED_OUT_BY)[0];
  const referredToBy = normalizeFieldToArray(producer, REFERRED_TO_BY);

  if (carriedOutBy == undefined) {
    return undefined;
  }

  // add id to producer object if producer is not Unknown
  if (isMakerKnown(carriedOutBy) === true) {
    context.id = getObjId(carriedOutBy);
    context.type = carriedOutBy.type;
  }

  // add all_names field using Person LOD
  if (context.id && people != undefined) {
    // use person LOD to get fields used for elasticsearch
    const idMatch = people.filter((object) => {
      return (
        carriedOutBy != undefined &&
        object != undefined &&
        getObjId(object) == context.id
      );
    });
    const person = idMatch[0];
    if (person != undefined) {
      // set default values based on the person
      context.allNames = getAllNames(person);
      context.name = getName(person);
      context.slug_with_path = routerHelpers.getSlugWithPath(person);
      context.nationality_and_dates = getValueByClassification(
        person.referred_to_by,
        internal.ARTIST_NATIONALITY_AND_DATE
      );
    }
  }
  // add primary_name to producer object use this above the artist name
  let name = getValueByClassification(referredToBy, internal.PRODUCER_NAME);
  if (name != undefined) {
    context.name = name;
  }

  context.prefix = getValueByClassification(referredToBy, aat.NAME_PREFIX);

  // use the production object if it exists over the artist object
  let prefix = getValueByClassification(
    referredToBy,
    internal.PRODUCER_NAME_PREFIX
  );

  if (prefix != undefined) {
    context.beforeLink = prefix;
  }

  // use the production object if it exists over the artist object
  let nationality_and_dates = getValueByClassification(
    referredToBy,
    internal.NATIONALITY_AND_DATES
  );

  if (nationality_and_dates != undefined) {
    context.nationality_and_dates = nationality_and_dates;
  }

  let description = getValueByClassification(
    referredToBy,
    internal.PRODUCER_DESCRIPTION
  );

  if (description == undefined && isMakerKnown(carriedOutBy) === false) {
    description = getValueByClassification(referredToBy, aat.PREFERRED_TERMS);
  }

  context.description = description;

  let afterLink = getValueByClassification(
    referredToBy,
    internal.PRODUCER_NAME_SUFFIX
  );

  if (context.nationality_and_dates != undefined) {
    afterLink = afterLink ? `${afterLink} ` : "";
    if (localize) {
      afterLink += `(${context.nationality_and_dates.trim()})`;
    } else {
      afterLink += context.nationality_and_dates.trim();
    }
  }
  context.afterLink = afterLink;

  context.role = getProducerRole(producer, aatQualifiers);
  if (context.role == undefined) {
    if (getClassifiedAs(producer, aat.FORMER_ATTRIBUTION)) {
      const aatVal = findAndGetAATValue({
        id: aat.FORMER_ATTRIBUTION,
        qualifiers: aatQualifiers
      });

      if (aatVal) {
        roles.push(convertToTitleCase(aatVal));
      }
    }
  }

  return removeEmpty(context);
}

/**
 * Gets the accession # sort field
 *
 * @param {Object} - the human made object
 *
 * @returns {String}
 */
export function getAccessionNumberSort(object) {
  if (object == undefined) {
    return undefined;
  }

  return getValueByClassification(
    object[DIMENSION],
    internal.ACCESSION_NUMBER_SORT
  );
}

/**
 * Gets the unique Alternate Numbers for the humanMadeObject
 *
 * @param {object} object  - the HumanMadeObject
 * @param {function} translate - the translate function from Vue18n
 *
 * @returns {array} a list of numbers for display including qualifiers
 */
export function getAlternateNumbers(object, translate) {
  let objectNumber = getObjectNumber(object);
  let accessionNumber = getAccessionNumber(object);
  let manuscriptNumber = getManuscriptNumber(object, aat.MANUSCRIPT_NUMBER);
  let alternateNumbers = [];
  if (objectNumber != accessionNumber && accessionNumber != undefined) {
    alternateNumbers.push(
      translate("object_metadata.alt_object_number", {
        number: accessionNumber
      })
    );
  }
  if (manuscriptNumber != undefined) {
    alternateNumbers.push(
      translate("object_metadata.alt_manuscript_number", {
        number: manuscriptNumber
      })
    );
  }
  return alternateNumbers;
}

/**
 * Checks to see if a HumanMadeObject is a fragment
 *
 * @param {object} object - the HumanMadeObject JSON-LD Object
 *
 * @returns {boolean}
 */
export function isFragment(object) {
  const fragmentClassification = getClassifiedAs(object, aat.FRAGMENTS);
  return fragmentClassification.length > 0;
}

/**
 * Whether to display the BookViewer interface or not
 *
 * @param {object} object - the JSON-LD Object
 *
 * @returns {boolean}
 */
export function showBookViewer(object) {
  if (object == undefined) {
    return false;
  }

  if (getClassifiedAs(object, aat.BOOKS).length > 0) {
    return true;
  }
  return false;
}

/**
 * Checks to see if a HumanMadeObject is a ComplexObject and returns a type for display
 *
 * @param {object} object - the HumanMadeObject JSON-LD Object
 *
 * @return {string} ComplexObject type string or undefined
 */

export function getComplexObjectType(object) {
  if (showBookViewer(object)) {
    return MANUSCRIPT;
  } else if (isFragment(object)) {
    return FRAGMENT;
  }
  return SET;
}

/**
 * Takes a HumanMadeObject and checks the rights statement.
 * Returns true if the statement is No Copyright. Returns false otherwise.
 * TO DO: Find out if No Copyright is the only status that allows for open content
 * @param {object} object - The JSON-LD for HumanMadeObject
 */
export function getOpenContentBoolean(object) {
  const referred = normalizeFieldToArray(object, REFERRED_TO_BY);
  if (referred.length == 0) return false;
  const rights = getClassifiedAs(
    normalizeFieldToArray(object, REFERRED_TO_BY),
    aat.RIGHTS_STATEMENT
  );
  // fixme: DEV-8439 we need to account for the "https" versions of the rights statement urls to OPEN_CONTENT_LICENSES in linkedart
  let rightsIds = rights.map((obj) => obj.id.replace("https", "http"));
  return summarizeLicenses(rightsIds) == "open";
}

/**
 * gets the order of the object by checking if dimension has a value
 * @param {object} object  - the JSON-LD Object
 *
 * @returns {string} order if available
 */
function getOrder(object) {
  // if the object has a dimension, return the value; if not, return undefined
  return object.dimension ? object.dimension[0].value : undefined;
}

/**
 * Gets the parent ID (indexed ID) for a HumanMadeObject (if applicable)
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 *
 * @return {string} The parent ID (example: "object/f112e473-f35b-441f-9adb-7cbdc94992c8")
 *
 */
export function getParentID(object) {
  const partOf = normalizeFieldToArray(object, PART_OF);
  const parentID = partOf.length ? getIndexedId(partOf[0].id) : undefined;
  return parentID;
}

/**
 * gets the current location
 *
 * @param {object} object  - the JSON-LD Object
 *
 * @returns {array} the place object stub found in current_location
 */
export function getCurrentLocations(object) {
  let parts = [...normalizeFieldToArray(object, "part")];

  let locations = [];
  if (object == undefined) {
    return locations;
  }
  let ids = [];
  parts.forEach((part) => {
    if (part && CURRENT_LOCATION in part) {
      let location = part[CURRENT_LOCATION];
      if (!ids.includes(location.id)) {
        locations.push(location);
        ids.push(location.id);
      }
    }
  });

  // to support backwards compatibility -- the LOD will contain data in both places
  // so, if we have data already, then return
  if (locations.length > 0) {
    return locations;
  }

  if (CURRENT_LOCATION in object) {
    locations.push(object[CURRENT_LOCATION]);
  }

  return locations;
}

/**
 * gets the current keeper
 *
 * @param {object} object  - the JSON-LD Object
 *
 * @returns {array} the list of keeper stub
 */
export function getCurrentKeepers(object) {
  return normalizeFieldToArray(object, "current_keeper");
}

/**
 * gets the current owner
 *
 * @param {object} object  - the JSON-LD Object
 *
 * @returns {object} the owner stub
 */
export function getCurrentOwner(object) {
  return normalizeFieldToArray(object, "current_owner");
}

//FIXME: did the data change? currently returning a list of undefineds
/**
 * Takes a reference to a HumanMadeObject and returns the list of places depicted.
 * @param {object} personRef - The reference to a HumanMadeObject from JSON-LD
 * @return {array} returns a list of Strings that have the Place
 */
export function getPlaceDepicted(obj) {
  const shows = normalizeFieldToArray(obj, "shows");
  return getSharedPlaceDepicted(shows, internal.PLACE_DEPICTED);
}

/**
 * Takes a reference to a HumanMadeObject and returns the place found (as array).
 * @param {object} personRef - The reference to a HumanMadeObject from JSON-LD
 * @return {array} returns a list of Strings that have the Place
 */
export function getPlaceFound(obj) {
  const found = getValuesByClassification(
    normalizeFieldToArray(obj, REFERRED_TO_BY),
    internal.PLACE_FOUND
  );
  return found;
}

/**
 * Takes a HumanMadeObject and returns an array of producer objects
 * Producer objs are found under a HumanMadeObject's produced_by field, and contains referred_to_by, carried_out_by and/or timespan
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @return {array} Array of producer objects { referred_to_by: [], carried_out_by: [], timespan: {} }
 */
export function getProducers(object) {
  if (object == undefined) {
    return [];
  }
  const producedBy = object[PRODUCED_BY];
  if (!producedBy) return [];

  // handle objects that have a single and multiple producers the same
  let producers = normalizeFieldToArray(producedBy, PART);

  // if there is a "part" array, return that as the producers
  // if there is no "part" array, place the contents in an array
  if (!producers.length) {
    producers = [producedBy];
  }

  return producers;
}

/**
 * Takes a Producer object and returns the aat role values
 *
 * @param {object} producer - The Producer object from a HumanMadeObject's produced_by field (as returned by getProducers())
 * @param {array} aatQualifiers - an array of AAT JSON-LD Documents to reference
 * @param {string} lang_ - the language to get the aat values in
 *
 * @return {array} Array of string roles
 */
export function getProducerRole(producer, aatQualifiers, lang_) {
  // get referred_to_by of carried_out_by
  const carriedOutBy = normalizeFieldToArray(producer, CARRIED_OUT_BY);
  const referredToBy = normalizeFieldToArray(carriedOutBy[0], REFERRED_TO_BY);

  let roles = [];

  // get objects in referredToBy that are classified as roles
  const referredToByRoles = getClassifiedAs(
    referredToBy,
    internal.PRODUCER_ROLE
  );

  referredToByRoles.forEach((role) => {
    // role aat ID is first item in close_match array
    const id = role.close_match[0].id;

    // get role aat value in requested language
    const aatVal = findAndGetAATValue({
      id: id,
      qualifiers: aatQualifiers,
      lang: lang_
    });

    if (aatVal) {
      // if the data is from AAT, we get the singular version and lowercased
      roles.push(convertToTitleCase(aatVal));
    } else {
      // if the data is from the "content" field, it's plural and already title-cased
      let roleText = getValueOrContent(role);
      // remove any parenthetical qualifiers
      if (roleText.indexOf("(") > -1) {
        roleText = roleText.substring(0, roleText.indexOf("(")).trim();
      }
      // try and make it singular
      if (roleText.endsWith("s")) {
        roleText = roleText.substring(0, roleText.length - 1);
      }
      roles.push(roleText);
    }
  });

  return roles;
}

/**
 * Takes an array of related media references and adds an audio url if found in linguisticObjects store
 *
 * @param {array} linguisticObjects - array of JSON-LD Linguistic Objects
 * @param {array} aatQualifiers - aat entries to resolve
 *
 * @return {array} Updated array of related media references arrays with urls if available
 *
 */
export function getRelatedMediaDetails(linguisticObjects, _aatQualifiers) {
  if (!linguisticObjects || !linguisticObjects.length) {
    return [];
  }
  return linguisticObjects.map((lobject) => {
    const translations = normalizeFieldToArray(lobject, TRANSLATION);
    return translations.map((translation) => ({
      filename: getMediaReferenceFilename(translation),
      title: getMediaReferenceTitle(translation),
      languageId: getMediaReferenceLanguageId(translation),
      date: getMediaReferenceDate(translation),
      language: lookupIsoFromAat(
        normalizeFieldToArray(translation, LANGUAGE)[0].id
      ),
      url: getMediaReferenceDigitallyCarriedByField(translation, ACCESS_POINT),
      format: getMediaReferenceDigitallyCarriedByField(translation, FORMAT),
      duration: getMediaReferenceDuration(translation)
    }));
  });
}

/**
 * Gets the Related Works to an item based on the LinkedData relationships
 * @param {object} object  -the human made object
 *
 * @returns {array} - the array of ids for related works in sequence order
 */
export function getRelatedWorks(object) {
  let relations = normalizeFieldToArray(object, "related_from_by");
  if (relations.length == 0) {
    return relations;
  }

  let tempWorks = [];
  let toReturn = [];
  let max = 0;
  // filter our the objects in order
  relations.forEach((relation) => {
    let pos = getValueByClassification(
      relation.dimension,
      aat.SEQUENCE_POSITION
    );

    // construct temporary objects to maintain order
    tempWorks.push({
      id: getIndexedId(relation.relates_to.id),
      position: pos
    });
    // capture the max #
    if (pos > max) {
      max = pos;
    }
  });

  // put in the right order
  toReturn = new Array(max);
  tempWorks.forEach((work) => {
    toReturn[work.position - 1] = work.id;
  });
  return toReturn;
}

/**
 * Takes a HumanMadeObject and get the rights statement.
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 *
 * @return {string} The rights statement content
 *
 */
export function getRightsStatement(object) {
  return getSharedRightsStatement(object, aat.RIGHTS_STATEMENT);
}

/**
 * Takes a HumanMadeObject and get the rights statement id/url.
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 *
 * @return {string} The rights statement id/url
 *
 */
export function getRightsStatementId(object) {
  return getSharedRightsStatementID(object, aat.RIGHTS_STATEMENT);
}

/**
 * Takes a HumanMadeObject and get an object containing the data needed for the HumanMadeObject home page tabs.
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 * @param {object} linguisticObjects - array of linguistic objects from store/state
 * @return {object} The tabs data
 *
 */
export function getTabsData(object) {
  return {
    exhibitions: getExhibitionIDs(object),
    bibliography: getBibliographicTabData(object),
    education_resources: getEducationResourcesTabData(object)
  };
}

/**
 * Takes a reference to a Maker and checks if they are known or unknown. Returns true or false.
 * @param {object} personRef - The reference to a person from JSON-LD for HumanMadeObject
 * @return {boolean} True if the person is known, false if the person is unknown
 */
export function isMakerKnown(makerRef) {
  let classifiedAsUnknown = getClassifiedAs(
    makerRef,
    [
      internal.UNKNOWN_MAKER,
      "https://data.getty.edu/museum/ontology/linked-data/tms/object/unknown-maker"
    ],
    { operator: "OR" }
  );
  if (
    classifiedAsUnknown.length ||
    // FIXME -- this is a HACK until DEV-12212 is fixed, check again 2022-07-01
    (makerRef &&
      makerRef.id &&
      makerRef.id.includes("person/25936a78-da08-44b5-b237-9bed6da06204"))
  ) {
    return false;
  }
  return true;
}

/**
 * Takes a HumanMadeObject and returns the value of the first Object Hierarchy Sequence: Position
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 *
 * @return {integer|undefined}
 *
 */
export function getSiblingOrderNumber(object) {
  let dimensions = normalizeFieldToArray(object, DIMENSION);
  let orderNumbers = getClassifiedAs(
    dimensions,
    internal.OBJECT_HIERARCHY_SEQUENCE
  );

  return getValueByClassification(
    orderNumbers,
    internal.OBJECT_HIERARCHY_SEQUENCE
  );
}

/**
 * Gets the number of parts (children) of a HumanMadeObject
 *
 * @param {object} object - the JSON-LD for the HumanMadeObject
 *
 * @return {integer|undefined}
 */
export function getNumberOfParts(object) {
  let dimensions = normalizeFieldToArray(object, DIMENSION);
  let numParts = getClassifiedAs(dimensions, internal.COMPONENTS_OBJECT_PARTS);
  return getValueByClassification(numParts, internal.COMPONENTS_OBJECT_PARTS);
}

/**
 * Takes a HumanMadeObject and checks if it is a part (of a complex object)
 *
 * @param {object} object - The JSON-LD for HumanMadeObject
 *
 * @return {boolean}
 *
 */
export function isPart(object) {
  if (object.part_of) {
    return true;
  }

  return false;
}

function isValid(fieldValue) {
  if (
    fieldValue == undefined ||
    fieldValue == "" ||
    Object.keys(fieldValue).length == 0 ||
    fieldValue.length == 0
  ) {
    return false;
  }
  return true;
}

/**
 * Gets the list of id/maker names for Unknown makers
 * @param {Object} humanMadeObject
 *
 * @returns {array} - list of unknown creators
 */
export function getUnknownMakers(humanMadeObject) {
  let toReturn = [];
  if (
    humanMadeObject == undefined ||
    humanMadeObject.produced_by == undefined
  ) {
    return [];
  }
  let parts = [humanMadeObject.produced_by];

  if (humanMadeObject.produced_by.part) {
    parts = humanMadeObject.produced_by.part;
  }
  for (let part of parts) {
    if (
      part.carried_out_by &&
      getClassifiedAs(
        part.carried_out_by,
        [
          internal.UNKNOWN_MAKER,
          "https://data.getty.edu/museum/ontology/linked-data/tms/object/unknown-maker"
        ],
        { operator: "OR" }
      ).length <= 0
    ) {
      continue;
    }
    toReturn.push({
      name: getValueByClassification(
        part.referred_to_by,
        internal.PRODUCER_DESCRIPTION
      ),
      id: internal.UNKNOWN_MAKER_INDEX_KEY
    });
  }
  return toReturn;
}

/**
 * Given a list of of sparse creators, a list of creators, and a list of unknown creators, this reconciles the two lists by constructing a new list in the original order but with real objects
 * @param {array} creators - the list of sparse creators
 * @param {array} results - the list of hydrated (real) creators
 * @param {array} unknown - the list of unknown creators
 *
 * @returns {array} - the list of resolved creators in the order specified by the creators array, leaving out any not found
 */
export function constructArtistOrder(creators, _results, _unknown) {
  let cresult = [];
  if (Array.isArray(creators) == false) {
    return [];
  }
  let results = [];
  let unknown = [];
  if (Array.isArray(_results)) {
    results = _results;
  }
  if (Array.isArray(unknown)) {
    unknown = _unknown;
  }
  // for all of the creators, put the known and unknown in order
  for (let creator of creators) {
    // if the id matches a known person, use it
    let matches = results.filter(
      (r) => r != undefined && creator.id.endsWith(r.id)
    );
    // if we found the person
    if (matches.length > 0) {
      cresult.push(matches[0]);
    } else if (
      getClassifiedAs(
        creator,
        [
          internal.UNKNOWN_MAKER,
          "https://data.getty.edu/museum/ontology/linked-data/tms/object/unknown-maker"
        ],
        { operator: "OR" }
      ).length > 0
    ) {
      // otherwise append the unknown artist in the right spot
      cresult.push(unknown.shift());
    }
  }
  return cresult;
}

/**
 * Extracts the Width, Depth, Height, and Weight from a HumanMadeObject's dimensions. Normalizes the results to Killograms and Centimeters
 * @param {object} humanMadeObject
 *
 * @returns {object} an object with a width, height, depth
 */
export function getNumericDimensions(humanMadeObject) {
  // FYI / FIXME: https://jira.getty.edu/browse/DEV-10437 if programmatic dimensions ever become important
  // we will need to adjust the logic to find the right 'set'
  let dimensions = humanMadeObject[DIMENSION];
  let width = getClassifiedAs(dimensions, aat.WIDTH);
  let height = getClassifiedAs(dimensions, aat.HEIGHT);
  let depth = getClassifiedAs(dimensions, aat.DEPTH);
  let weight = getClassifiedAs(dimensions, aat.WEIGHT);
  let ret = {};
  if (width && width.length > 0) {
    let unit = width[0].unit.id;
    let val = width[0].value;
    if (unit == aat.MILLIMETERS) {
      val = val / 10;
    }
    ret["width"] = val;
  }

  if (weight && weight.length > 0) {
    let unit = weight[0].unit.id;
    let val = weight[0].value;
    if (unit == aat.METRIC_TONS) {
      val = val * 1000;
    }
    if (unit == aat.GRAMS) {
      val = val / 1000;
    }
    ret["weight"] = val;
  }

  if (height && height.length > 0) {
    let unit = height[0].unit.id;
    let val = height[0].value;
    if (unit == aat.MILLIMETERS) {
      val = val / 10;
    }
    ret["height"] = val;
  }
  if (depth && depth.length > 0) {
    let unit = depth[0].unit.id;
    let val = depth[0].value;
    if (unit == aat.MILLIMETERS) {
      val = val / 10;
    }
    ret["depth"] = val;
  }
  return ret;
}

/**
 * Extracts the Visual Item stubs from a HumanMadeObject
 * @param {object} humanMadeObject
 * @param {object} stubBase - the base object to be extended with each ID (e.g. {type, path, baseUrl})
 *
 * @returns {array} an array of all the visual item
 */
export function getVisualItemStubs(humanMadeObject, stubBase = {}) {
  let visualItemStubs = [];
  normalizeFieldToArray(humanMadeObject, VISUAL_ITEM_FIELD).forEach((show) => {
    // if we don't have a 'format' or if we have an about or referred to by
    if (
      show.format == undefined ||
      show.about != undefined ||
      show.referred_to_by != undefined
    ) {
      return;
    }
    let itemId = getIndexedId(show.id.replace(".json", ""));
    visualItemStubs.push({ id: itemId, ...stubBase });
  });
  return visualItemStubs;
}

/**
 * Extracts the Related Media stubs from a id manager request
 * @param {object} mediaReferences - array of related media references from ID manager
 * @param {object} stubBase - the base object to be extended with each ID (e.g. {type, path, baseUrl})
 *
 * @returns {array} an array of all the related audio
 */
export function getRelatedMediaStubs(mediaReferences, stubBase) {
  let relatedMediaStubs = [];
  normalizeFieldToArray(mediaReferences, ITEMS).forEach((item) => {
    let itemId = getIndexedId(item.target.id);
    relatedMediaStubs.push({ id: itemId, ...stubBase });
  });
  return relatedMediaStubs;
}

/**
 * Gets the Image Service URL from the HumanMadeObject Thumbnail used for the Image Form's OTMM Matching
 *
 * @param {object} humanMadeObject
 *
 * @returns  {String}
 */
export function getIiifImageServiceId(humanMadeObject) {
  let thumb_service = getThumbnailUrl(humanMadeObject);
  if (thumb_service.indexOf("/full")) {
    thumb_service = thumb_service.substring(0, thumb_service.indexOf("/full"));
  }
  return thumb_service;
}

/**
 * Traverses the HumanMadeObject to extract out all of the indexedIds for constituents that purchased the object
 *
 * @param {object} humanMadeObject
 *
 * @returns {array} a list of indexedIds
 */
export function getTransferredToIndexedIds(humanMadeObject) {
  let changedOwnership = normalizeFieldToArray(
    humanMadeObject,
    "changed_ownership_through"
  );
  let indexedIds = [];
  changedOwnership.forEach((activity) => {
    let constituents = normalizeFieldToArray(activity, "transferred_title_to");
    constituents.forEach((constituent) => {
      let indexedId = getIndexedId(constituent.id);
      if (indexedId in indexedIds == false) {
        indexedIds.push(indexedId);
      }
    });
  });
  return indexedIds;
}

/**
 * Gets the related links for the object
 * @param {object} - the HumanMadeObject
 *
 * @returns {array}
 */
export function getRelatedLinks(humanMadeObject) {
  // add essay links
  const essayLinks = getEssayLinks(humanMadeObject);
  return [...essayLinks];
}

/**
 * Parses through the related media data and returns object with heading
 * and transformed array of media references as children
 * @param {array} mediaReferences - an array of arrays of related media data objects to parse
 * @param {string} language - (default) ISO language
 *
 * @returns {array} - an array of the related media data that has been sorted/grouped by filename
 */
export function groupRelatedMediaFiles(mediaReferences, language = "en") {
  if (
    !mediaReferences ||
    !mediaReferences.length ||
    mediaReferences.every((ref) => !ref.length)
  ) {
    return [];
  }

  // include heading/label and shortTitle for each group of references
  mediaReferences = mediaReferences.map((translations) => {
    return addMediaGroupHeadingShortTitle(translations);
  });

  // alphabetize children of groups by language name and put requested language up front
  return alphabetizeMediaGroups(mediaReferences, language);
}

/**
 * [Helper function for above groupRelatedMediaFiles function]
 * Takes an array of media reference groups and maps through each group of children and:
 * a) alphabetizes the files by language
 * b) checks what the default/browser language is and places that on top
 * @param {array} mediaGroups -  an array of groups of media references
 * @param {language} language - (default) language to place at the top of each group
 *
 * @returns {array} an array of groups that have been alphabetized/sorted
 */
function alphabetizeMediaGroups(mediaGroups, language = "en") {
  return mediaGroups.map((group) => {
    if (group.children.length == 1) {
      return group;
    }

    let alphabetizedChildren = group.children.sort((a, b) => {
      if (a.language) {
        return a.language.localeCompare(b.language);
      }
      return;
    });

    // check if there is a child matching the language passed in and put that first in the list
    let languageAat = lookupAatFromIso(language);
    let defaultLanguageFile = alphabetizedChildren.filter(
      (child) => child.languageId == languageAat
    )[0];
    if (defaultLanguageFile) {
      alphabetizedChildren = alphabetizedChildren.filter(
        (child) => child.languageId != languageAat
      );
      alphabetizedChildren.unshift(defaultLanguageFile);
    }

    return {
      heading: group.heading,
      children: alphabetizedChildren
    };
  });
}

/**
 * [Helper function for above groupRelatedMediaFiles function]
 * Parses through media references and determines what the label/heading should be
 * - adds heading/label
 * - adds shortTitle to child media references
 * @param {object} mediaReferences - array of mediar references
 *
 * @returns {object} - returns object with heading/label and media references array as children
 */
export function getMediaGroupHeadingShortTitle(mediaReferences) {
  if (!mediaReferences || !mediaReferences.length) {
    return [];
  }
  // if more than one file, find common string among all
  let commonTitle =
    mediaReferences.length == 1
      ? mediaReferences[0].title
      : sharedString(mediaReferences.map((reference) => reference.title));

  mediaReferences.forEach((reference) => {
    reference.shortTitle = reference.title.substring(commonTitle.length);
    while (reference.shortTitle.match(/^[\-\s\(]/)) {
      reference.shortTitle = reference.shortTitle.substring(1).trim();
    }

    // check balance of ()
    if (
      reference.shortTitle.indexOf(")") > -1 &&
      reference.shortTitle.indexOf("(") < 0
    ) {
      reference.shortTitle = reference.shortTitle.replace(")", "");
    }
  });

  // check balance of ()
  let countOpen = commonTitle.split("(").length;
  let countClosed = commonTitle.split(")").length;
  if (countClosed < countOpen) {
    commonTitle += ")";
  }

  return commonTitle;
}

/**
 * [Helper function for above groupRelatedMediaFiles function]
 * Finds the common/shared string among an array of strings
 * @param {array} strings - an array of strings to parse through
 *
 * @returns {string} - the common string among all the strings passed in
 */
function sharedString(strings) {
  let sortedStrings = strings.concat().sort();
  let first = sortedStrings[0];
  let last = sortedStrings[sortedStrings.length - 1];
  let firstLength = first.length;
  let i = 0;
  while (i < firstLength && first.charAt(i) === last.charAt(i)) {
    i++;
  }
  // trim if it ends with space or punctuation
  while (
    first.substring(0, i).endsWith(" ") ||
    first.substring(0, i).endsWith("-") ||
    first.substring(0, i).endsWith("(")
  ) {
    i--;
  }
  return first.substring(0, i).trim();
}

/**
 * Parses translated media object from linguistic object and returns filename
 * @param {object} translatedMediaReference - translated media object reference from JSON-LD
 *
 * @returns {string|undefined} file name of translated media reference from JSON-LD
 */
export function getMediaReferenceFilename(translatedMediaReference) {
  let identified_by = normalizeFieldToArray(
    translatedMediaReference,
    IDENTIFIED_BY
  );
  let filename = getValueByClassification(identified_by, internal.FILE_NAME);
  if (!filename) {
    return;
  }
  return filename;
}

/**
 * Parses translated media object from linguistic object and returns date
 * @param {object} translatedMediaReference - translated media object reference from JSON-LD
 *
 * @returns {string|undefined} file name of translated media reference from JSON-LD
 */
export function getMediaReferenceDate(translatedMediaReference) {
  let identified_by = normalizeFieldToArray(
    translatedMediaReference,
    IDENTIFIED_BY
  );
  let filename = getValueByClassification(identified_by, internal.FILE_NAME);
  if (!filename) {
    return;
  }

  let start = -1;
  if (filename.indexOf("-20") > -1) {
    start = filename.indexOf("-20");
  }
  if (filename.indexOf("-19") > -1) {
    start = filename.indexOf("-19");
  }
  if (start < 0) {
    return;
  }

  // just return the year for now
  let date = filename.substr(start + 1, 4);

  return date;
}

/**
 * Parses translated media object from linguistic object and returns AAT language string
 * @param {object} translatedMediaReference - translated media object reference from JSON-LD
 *
 * @returns {string|undefined} normalized AAT language string of translated media reference from JSON-LD
 */
export function getMediaReferenceLanguage(
  translatedMediaReference,
  aatQualifiers
) {
  let language = normalizeFieldToArray(translatedMediaReference, LANGUAGE);
  if (!language || !language.length) {
    return;
  }
  let lang = findAndGetAATValue({
    id: language[0].id,
    qualifiers: aatQualifiers
  });
  return lang;
}

/**
 * Parses translated media object from linguistic object and returns AAT language id string
 * @param {object} translatedMediaReference - translated media object reference from JSON-LD
 *
 * @returns {string|undefined} normalized AAT language id string of translated media reference from JSON-LD
 */
export function getMediaReferenceLanguageId(translatedMediaReference) {
  let lang = normalizeFieldToArray(translatedMediaReference, LANGUAGE);
  if (!lang || !lang.length) {
    return;
  }
  return lang[0].id;
}

/**
 * Parses translated media object from linguistic object and returns title from descriptive text
 * @param {object} translatedMediaReference - translated media object reference from JSON-LD
 *
 * @returns {string|undefined} title of translated media reference from JSON-LD
 */
export function getMediaReferenceTitle(translatedMediaReference) {
  let referred_to_by = normalizeFieldToArray(
    translatedMediaReference,
    REFERRED_TO_BY
  );

  let title = getValueByClassification(referred_to_by, aat.DESCRIPTIVE_NOTE);
  if (!title) {
    return;
  }
  return title;
}

/**
 * Gets the media duration for an audio file for the translation reference
 *
 * @param {object} translatedMediaReference - the translated media object reference from JSON-LD
 *
 * @returns {string} the duration in minutes:seconds
 */
export function getMediaReferenceDuration(translatedMediaReference) {
  let dimensions = getMediaReferenceDigitallyCarriedByField(
    translatedMediaReference,
    DIMENSION
  );
  let dimension = getClassifiedAs(dimensions, aat.DURATION);
  if (dimension.length < 1) {
    return undefined;
  }
  let statements = normalizeFieldToArray(dimension[0], REFERRED_TO_BY);
  let statement = getValueByClassification(statements, aat.DURATION);
  return statement;
}

/**
 * Parses translated media object from linguistic object and returns digitally_carried_by field
 * @param {object} translatedMediaReference - translated media object reference from JSON-LD
 * @param {string} field - digitally_carried_by field from JSON-LD
 *
 * @returns {string|undefined} description of translated media url from JSON-LD
 */
export function getMediaReferenceDigitallyCarriedByField(
  translatedMediaReference,
  field
) {
  let digitally_carried_by = normalizeFieldToArray(
    translatedMediaReference,
    DIGITALLY_CARRIED_BY
  );
  if (
    !digitally_carried_by.length ||
    !digitally_carried_by[0][field] ||
    !digitally_carried_by[0][field].length
  ) {
    return;
  }
  let digitallyCarriedByField =
    field == ACCESS_POINT
      ? digitally_carried_by[0][field][0].id
      : digitally_carried_by[0][field];
  if (!digitallyCarriedByField) {
    return;
  }
  return digitallyCarriedByField;
}

/**
 * Formats short title
 * @param {string} title - media reference title
 *
 * @returns {string} shortTitle - shortened title
 */
export function formatMediaReferenceShortTitle(title) {
  let shortTitle = title.substring(commonTitle.length);
  while (reference.shortTitle.match(/^[\-\s\(]/)) {
    reference.shortTitle = reference.shortTitle.substring(1).trim();
  }
  return shortTitle;
}

/**
 * PLACEHOLDER getMetadata HELPER
 *
 * Gets formatted subject tags
 * @param {object} object  - the human made object
 *
 * @returns {array} tags - array of subject tags
 */
export function getSubjectTags(_object) {
  // because linter says I have to do something with
  // this object ¯\_(ツ)_/¯
  let tags = []; //object.type, "Test", "Subject", "Tags"];
  return tags;
}

/**
 * Given an array of artist objects, filters duplicate entries based on a shared value
 * @param {object} artists - the array of artist objects
 * @param {string} key - the common object key by which to identify duplicate entries
 *
 * @returns {object} description of translated media url from JSON-LD
 */
export function dedupeArtists(artists, key) {
  let seen = [];
  let filteredArtists = artists.filter((artist) => {
    const isDuplicate = seen.includes(artist[key]);
    if (!isDuplicate) {
      seen.push(artist[key]);
      return true;
    }
    return false;
  });
  return filteredArtists;
}

/**
 * Gets the object record structure type
 * @param {object} object  - the JSON-LD Object
 *
 * @returns {string|undefined}
 */
export function getRecordStructure(object) {
  let recordStructure = getClassifiedAsWithClassification(
    object,
    internal.RECORD_STRUCTURE_TYPE
  );

  return recordStructure[0] && recordStructure[0].id
    ? recordStructure[0].id
    : undefined;
}

/**
 * Gets the locale key suffix for whether something is a group or an object with parts
 *
 * @param {object} object - the human made object
 *
 * @returns {string} the locale key suffix
 */
export function getRecordStructureLocaleKey(object) {
  let recordStructure = getRecordStructure(object);

  // a whole can have "parts"
  // a group, can have "wholes"
  // a whole that is a child is always part of a group

  if (recordStructure == internal.RECORD_STRUCTURE_PART) {
    // "part" (e.g., photo book page, manuscript leaf, diptych section)
    // when on the page of the parent or a sibling
    return PART_HEADING_LOCALE_KEY;
  }
  if (
    recordStructure &&
    [
      internal.RECORD_STRUCTURE_WHOLE,
      internal.RECORD_STRUCTURE_GROUP_WHOLE
    ].includes(recordStructure)
  ) {
    // "whole" (e.g., fragment, diptych section)
    return WHOLE_HEADING_LOCALE_KEY;
  }

  // "group" (e.g., book, group of fragments, diptych)
  return GROUP_HEADING_LOCALE_KEY;
}

/**
 * Gets heading for related parts of set for a complex object
 * @param {humanMadeObject} humanMadeObject - the human made object JSON-LD
 * @param {function} translate - the translate function
 *
 * @returns {String}
 */
export function getRelatedPartsOfSetHeading(humanMadeObject, translate) {
  let localeSubKey = getRecordStructureLocaleKey(humanMadeObject);

  // if the object is a "whole", and is part of something, then the parent must be a group
  if (localeSubKey == WHOLE_HEADING_LOCALE_KEY) {
    localeSubKey = GROUP_HEADING_LOCALE_KEY;
  }
  // if the object is a "part", and is part of something, then the parent must be a whole
  else if (localeSubKey == PART_HEADING_LOCALE_KEY) {
    localeSubKey = WHOLE_HEADING_LOCALE_KEY;
  }
  return translate("human_made_object_set.related." + localeSubKey);
}

/**
 * Helper to determine whether the HMO page should show the PartsOfSet component
 *
 * @param {boolean} hasParts - whether the complex object has parts
 * @param {boolean} hasAllObjects - whether this is a part and has siblings
 * @param {string} complexObjectType - the type of complex object it is
 * @param {boolean} partsHaveDistinctImages - whether the parts have distinct images
 * @param {boolean} isRoot - whether this is a root element
 *
 * @returns {boolean}
 */
export function shouldDisplayPartsOfSet(
  hasParts,
  hasAllObjects,
  complexObjectType,
  partsHaveDistinctImages,
  isRoot
) {
  if (hasParts == false && hasAllObjects == false) {
    return false;
  }
  if (
    ["fragment"].includes(complexObjectType) ||
    complexObjectType == undefined ||
    (["fragment", "set"].includes(complexObjectType) &&
      partsHaveDistinctImages == false &&
      isRoot)
  ) {
    return false;
  }
  return true;
}

/**
 * Derive valid Image Viewer labelSource param value from object classification
 *
 * @param {object} object - human made object
 * @returns {string} supported values ['default','range','all']
 */
export function getSequenceLabelSource(object) {
  const classifications = normalizeFieldToArray(object, CLASSIFIED_AS).map(
    (e) => e.id
  );
  let labelSource = "default";

  if (classifications.includes(aat.BOOKS)) {
    labelSource = "range";
  }

  if (
    // concatenated labels for manuscripts only for now
    classifications.includes(aat.BOOKS) &&
    classifications.includes(aat.ILLUMINATED_MANUSCRIPTS)
  ) {
    labelSource = "all";
  }

  return labelSource;
}

/**
 * Whether or not the current object is classified as deaccessioned
 * @param {object} object - human made object
 * @returns {boolean}
 */
export function isDeaccessioned(object) {
  return getClassifiedAs(object, aat.DEACCESSIONED).length > 0;
}

/**
 * Get deaccession statement from lod
 * @param {object} object - human made object
 * @returns {string}
 */
export function getDeaccessionStatement(object) {
  let referredToBy = normalizeFieldToArray(object, REFERRED_TO_BY);
  return getValueByClassification(referredToBy, internal.DEACCESSION_STATEMENT);
}

/**
 * Get long deaccession statement from lod
 * @param {object} object - human made object
 * @returns {string} - markdown
 */
export function getDeaccessionStatementLong(object) {
  return getContentWithFormat(
    object,
    REFERRED_TO_BY,
    internal.DEACCESSION_STATEMENT_LONG,
    "text/markdown"
  )[0];
}

/**
 * Gets the Deaccessioned group id
 *
 * @param {object} object - the JSON LD Object
 *
 * @returns  {array}
 */
export function getDeaccessionedGroupId(object) {
  let identifiedBy = normalizeFieldToArray(object, IDENTIFIED_BY);
  return getValuesByClassification(
    identifiedBy,
    internal.DEACCESSION_GROUP_NAME
  );
}
